import { dateformat } from "../project/dateFormat";
import { hasValue } from "lib/utils/hasValue";
import { authFetch } from "lib/api/authFetch";
import { BACKEND_URL } from "lib/utils/baseUrl";
import {
  FILE_DETAIL_FILTER_QUERY_KEYS,
  normalizeFileDetailFilters,
  serializeFileDetailFilterValues,
} from "lib/utils/fileDetailFilters";

export async function getFileList(
  projectId,
  {
    sortKey,
    sortDir,
    page = 1,
    limit = 20,
    category,
    index,
    value,
    treeBuildingName,
    treeFloor,
    treeLocation,
    treeFacilityType,
    treeFacilityName,
    treePartName,
    treeNodeId,
    include_visual = 0,
    excludeFileType,
    fileType,
    facilityType,
    fusionResult,
    searchCategory,
    searchKeyword,
  } = {},
) {
  if (!hasValue(projectId)) {
    console.warn("⚠️ [getFileList] Id 누락");
    return { success: false, message: "유효하지 않은 ID입니다." };
  }

  try {
    const safePage = Number(page) > 0 ? Number(page) : 1;
    const safeLimit = Number(limit) > 0 ? Number(limit) : 20;

    // ✅ query 생성
    const qs = new URLSearchParams();
    qs.set("page", String(safePage));
    qs.set("limit", String(safeLimit));
    if (hasValue(treePartName)) qs.set("treePartName", String(treePartName));
    if (hasValue(treeNodeId)) qs.set("treeNodeId", String(treeNodeId));
    if (hasValue(sortKey)) qs.set("sortKey", String(sortKey));
    if (hasValue(sortDir)) qs.set("sortDir", String(sortDir));
    if (hasValue(category)) qs.set("category", String(category));
    if (hasValue(index)) qs.set("index", String(index));
    if (hasValue(value)) qs.set("value", String(value));
    if (hasValue(treeBuildingName)) {
      qs.set("treeBuildingName", String(treeBuildingName));
    }
    if (hasValue(treeFloor)) {
      qs.set("treeFloor", String(treeFloor));
    }
    if (hasValue(treeLocation)) {
      qs.set("treeLocation", String(treeLocation));
    }
    if (hasValue(treeFacilityType)) {
      qs.set("treeFacilityType", String(treeFacilityType));
    }
    if (hasValue(treeFacilityName)) {
      qs.set("treeFacilityName", String(treeFacilityName));
    }
    if (hasValue(searchKeyword)) {
      qs.set("searchKeyword", String(searchKeyword));
      if (hasValue(searchCategory)) {
        qs.set("searchCategory", String(searchCategory));
      }
    }
    if (hasValue(excludeFileType)) {
      qs.set("excludeFileType", String(excludeFileType));
    }

    const detailFilters = normalizeFileDetailFilters({
      fileType,
      facilityType,
      fusionResult,
    });

    Object.entries(FILE_DETAIL_FILTER_QUERY_KEYS).forEach(
      ([filterKey, queryKey]) => {
        const value = serializeFileDetailFilterValues(detailFilters[filterKey]);
        if (hasValue(value)) qs.set(queryKey, value);
      },
    );

    qs.set(
      "include_visual",
      String(include_visual === 1 || include_visual === "1" ? 1 : 0),
    );

    // ✅ 경로(path)만 구성해서 전달 (env 기반 BACKEND_URL 사용)
    const path = `${BACKEND_URL}/project/file_list/${projectId}?${qs.toString()}`;

    // ✅ fetch -> authFetch
    const res = await authFetch(
      path,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
      {
        // ✅ 실제 refresh 경로에 맞춰 조정
        refreshUrlIncludes: "auth/refresh",
      },
    );

    // ✅ refresh까지 했는데도 401이면 세션 만료
    if (res.status === 401) {
      return {
        success: false,
        message: "인증이 만료되었습니다. 다시 로그인해 주세요.",
      };
    }

    const result = await res.json().catch(() => null);
    if (!result) {
      return { success: false, message: "서버 응답 파싱 실패" };
    }

    if (!res.ok || !result?.success) {
      console.warn(
        `[STEP 5-FAIL] : 서버 오류 또는 실패 응답 → ${
          result?.message || "조회 실패"
        }`,
      );
      return { success: false, message: result?.message || "조회 실패" };
    }

    const filesRaw = result.data?.files ?? [];
    const files = filesRaw.map((item) => ({
      ...item,
      upload_id: item?.upload_id ?? item?.id ?? "",
      uploaded_at: item.uploaded_at
        ? dateformat(item.uploaded_at).hasTimeDate
        : item.uploaded_at,
      shooting_date: item?.shooting_date
        ? dateformat(item.shooting_date).hasTimeDate
        : item?.shooting_date,
    }));

    const pagination = result.data?.pagination ?? {
      totalCount: 0,
      totalPages: 1,
      currentPage: safePage,
      limit: safeLimit,
    };

    return {
      success: true,
      message: result.message,
      files,
      pagination,
    };
  } catch (err) {
    console.error("❌ [getFileList] 요청 실패:", err);
    return { success: false, message: "서버 요청 중 오류가 발생했습니다." };
  }
}
